import type {ReactElement} from 'react';
import React, {useCallback, useMemo} from 'react';
import {SubtitlesEditor} from '@Components/poster-editor/components/poster-editor-subtitles-editor/components/subtitles-editor/subtitles-editor';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {PosterEditorSubtitles} from './poster-editor-subtitles-editor.types';
import {useGetCurrentSubtitleObject} from '@Hooks/poster-editor/useGetCurrentSubtitleObject';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {useGetActiveTranscriptItemSubtitlesHashmap} from '@Hooks/poster-editor/useGetActiveTranscriptItemSubtitlesHashmap';

export function PosterEditorSubtitlesEditor(): ReactElement<any> | null {
  const activeTranscriptItem = useGetSelectedTranscriptItem();
  const currentSubtitle = useGetCurrentSubtitleObject();
  const isMobileVariant = useMemo(() => isEditorMobileVariant(), []);
  const reduxSubtitlesHashmap = useGetActiveTranscriptItemSubtitlesHashmap();

  const subtitlesData = useMemo((): PosterEditorSubtitles | null => {
    if (!reduxSubtitlesHashmap) {
      return null;
    }

    const subtitleObjectsHashmap: Record<string, SubtitleObject> = {};
    const entries = Object.entries(reduxSubtitlesHashmap);

    for (const [uid, subtitle] of entries) {
      subtitleObjectsHashmap[uid] = {
        ...subtitle,
        words: subtitle.words.map((w) => ({...w})),
      };
    }

    const subtitlesOrder = entries
      .sort(([, a], [, b]) => {
        const startTimeDiff = a.startTime - b.startTime;
        if (startTimeDiff !== 0) {
          return startTimeDiff;
        }

        return a.endTime - b.endTime;
      })
      .map(([uid]) => uid);

    return {
      subtitlesOrder,
      subtitlesHashmap: subtitleObjectsHashmap,
    };
  }, [reduxSubtitlesHashmap]);

  const onSubtitlesChange = useCallback(
    async (subtitles: PosterEditorSubtitles, isUndoable = true, updateRedux = true): Promise<void> => {
      const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
      if (!currentPage) {
        return;
      }

      const subtitlesDataHashmap = subtitles.subtitlesHashmap;

      await activeTranscriptItem?.updateFromObject(
        {
          subtitlesHashmap: subtitlesDataHashmap,
        },
        {undoable: isUndoable, updateRedux}
      );
    },
    [activeTranscriptItem]
  );

  const seekToSubtitle = useCallback(
    (selectedId: string): void => {
      if (!activeTranscriptItem) {
        return;
      }

      void activeTranscriptItem.page.seek(activeTranscriptItem.subtitlesHashmap[selectedId].startTime);
    },
    [activeTranscriptItem]
  );

  if (activeTranscriptItem === null || subtitlesData === null) {
    return null;
  }

  return (
    <SubtitlesEditor
      selectedSubtitleId={currentSubtitle?.subtitleUID}
      setSelectedSubtitleId={seekToSubtitle}
      isMobileVariant={isMobileVariant}
      subtitles={subtitlesData}
      onSubtitlesChange={onSubtitlesChange}
    />
  );
}
